import {announce} from '@github-ui/aria-live'
import {Heading} from '@primer/react'
import {useNavigationType} from '@github-ui/react-router'
import {useEffect, useRef} from 'react'
import {getCountText} from '@github-ui/count'

import styles from './AIResourceSearchPage.module.css'

type SearchResultsHeaderProps = {
  elapsedMilliseconds?: number
  query: string
  status: 'success' | 'loading' | 'empty'
  totalCount?: number
}

export function SearchResultsHeader({elapsedMilliseconds, query, status, totalCount}: SearchResultsHeaderProps) {
  const navigationAction = useNavigationType()
  const headingRef = useRef<HTMLHeadingElement>(null)
  const previousStatusRef = useRef(status)
  let summary: string

  switch (status) {
    case 'success': {
      summary =
        totalCount === undefined
          ? `Results for ${query}`
          : `${getCountText(totalCount)} ${totalCount === 1 ? 'result' : 'results'} for ${query}`
      break
    }
    case 'loading':
      summary = `Loading AI resources for ${query}`
      break
    case 'empty':
      summary = `0 results for ${query}`
      break
  }

  useEffect(() => {
    const wasLoading = previousStatusRef.current === 'loading'
    previousStatusRef.current = status

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Focus follows completion of route-managed loading.
    if (wasLoading && status !== 'loading' && navigationAction !== 'POP') {
      headingRef.current?.focus()
      return
    }

    announce(summary)
  }, [navigationAction, status, summary])

  return (
    <div className={styles.ResultsHeader}>
      <Heading
        as="h2"
        className={styles.ResultsHeading}
        data-react-autofocus={status === 'loading' ? undefined : true}
        id="search-results-count"
        ref={headingRef}
        tabIndex={-1}
      >
        {summary}
      </Heading>
      {elapsedMilliseconds !== undefined && status !== 'loading' ? (
        <span className={styles.ResultsTime}>({formatElapsedTime(elapsedMilliseconds)})</span>
      ) : null}
    </div>
  )
}

function formatElapsedTime(elapsedMilliseconds: number) {
  return elapsedMilliseconds >= 1000 ? `${Math.round(elapsedMilliseconds / 1000)} s` : `${elapsedMilliseconds} ms`
}
